<template>
  <v-dialog :model-value="modelValue" width="520" persistent @update:model-value="v => $emit('update:modelValue', v)">
    <v-card>
      <v-card-title>Duplicate Gig</v-card-title>
      <v-card-text>
        <v-skeleton-loader v-if="isLoading" type="list-item-three-line@2" />

        <v-alert v-else-if="loadError" type="error" density="compact" class="mb-2">{{ loadError }}</v-alert>

        <v-form v-else-if="source" @submit.prevent="submit">
          <v-text-field v-model="name" label="Gig Name" class="mb-2" />
          <v-text-field
            v-model="newDate"
            label="New Date"
            type="date"
            required
            hint="Calls keep their times and their spacing from the gig date."
            persistent-hint
            class="mb-4"
          />

          <p class="text-body-2 mb-1">
            Copies {{ source.calls.length }} {{ source.calls.length === 1 ? 'call' : 'calls' }},
            {{ source.positions.length }} {{ source.positions.length === 1 ? 'position' : 'positions' }}<template
              v-if="carriedLines.length"
              >, and {{ carriedLines.length }} quoted {{ carriedLines.length === 1 ? 'line' : 'lines' }} (items and
              manual)</template
            >.
          </p>
          <p class="text-body-2 text-medium-emphasis mb-4">
            Offers and bookings, contracts and labor pricing are not copied. Positions start open; use "Recompute from
            Budget" on the new quote to price labor.
          </p>

          <!-- Only when a carried item's catalog price has moved (or the item is gone) -->
          <div v-if="diffs.length" class="mb-2">
            <v-alert type="warning" variant="tonal" density="compact" class="mb-3">
              Some quoted items have a different catalog price than when they were quoted.
            </v-alert>
            <v-table density="compact" class="mb-3">
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="text-right">Quoted</th>
                  <th class="text-right">Catalog now</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in diffs" :key="d.lineId">
                  <td>{{ d.description }}</td>
                  <td class="text-right">{{ money(d.legacyUnitCost) }}</td>
                  <td class="text-right">
                    <template v-if="d.missingFromCatalog"
                      ><span class="text-medium-emphasis">no longer in catalog — kept as quoted</span></template
                    >
                    <template v-else>{{ money(d.currentUnitCost ?? 0) }}</template>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <v-radio-group v-model="priceMode" hide-details="auto" class="mb-1">
              <v-radio value="legacy" label="Keep the prices as quoted on the original gig" />
              <v-radio value="current" label="Update to current catalog prices" />
            </v-radio-group>
            <p v-if="priceMode === 'current' && deltaBeforeTax !== 0" class="text-caption text-medium-emphasis ml-2">
              Quote subtotal {{ deltaBeforeTax > 0 ? 'increases' : 'decreases' }} by
              {{ money(Math.abs(deltaBeforeTax)) }} (before tax).
            </p>
          </div>

          <v-alert v-if="submitError" type="error" density="compact" class="mt-2">{{ submitError }}</v-alert>

          <div class="text-right mt-4">
            <v-btn variant="text" :disabled="isSaving" @click="close">Cancel</v-btn>
            <v-btn color="primary" type="submit" :loading="isSaving" :disabled="!canSubmit">Duplicate</v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { diffItemPrices } from '@gigdriver/domain';
import type { DuplicatePriceMode } from '@gigdriver/domain';
import { getGigDuplicateSource, getOrgItems } from '@gigdriver/client';
import type { GigDuplicateSource } from '@gigdriver/client';
import type { Item } from '@gigdriver/domain';
import { useGigsStore } from '../stores/gigs';
import { useOrganizationStore } from '../stores/organization';
import { useVenuesStore } from '../stores/venues';

const props = defineProps<{ modelValue: boolean; orgId: string; gigId: string }>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'duplicated', newGigId: string): void;
}>();

const gigsStore = useGigsStore();
const orgStore = useOrganizationStore();
const venuesStore = useVenuesStore();

const isLoading = ref(false);
const isSaving = ref(false);
const loadError = ref<string | null>(null);
const submitError = ref<string | null>(null);

const source = ref<GigDuplicateSource | null>(null);
const catalog = ref<Item[]>([]);
const name = ref('');
const newDate = ref('');
// Deliberately unset when a price decision is needed — the user must pick.
const priceMode = ref<DuplicatePriceMode | null>(null);

// Labor lines are derived from positions, not copied — only these carry.
const carriedLines = computed(() => (source.value?.quote?.lineItems ?? []).filter(l => l.source !== 'labor'));
const diffs = computed(() => diffItemPrices(carriedLines.value, catalog.value));

const deltaBeforeTax = computed(() => {
  let delta = 0;
  for (const d of diffs.value) {
    if (d.currentUnitCost === undefined) continue;
    const line = carriedLines.value.find(l => l.id === d.lineId);
    delta += (d.currentUnitCost - d.legacyUnitCost) * (line?.quantity ?? 1);
  }
  return Math.round(delta * 100) / 100;
});

const canSubmit = computed(
  () => !!source.value && !!newDate.value && !isSaving.value && (diffs.value.length === 0 || !!priceMode.value),
);

function money(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

watch(
  () => props.modelValue,
  async open => {
    if (!open) return;
    source.value = null;
    loadError.value = null;
    submitError.value = null;
    newDate.value = '';
    priceMode.value = null;
    isLoading.value = true;
    try {
      // Venues give the zone each call's times are shifted in.
      const [src, items] = await Promise.all([
        getGigDuplicateSource(props.orgId, props.gigId),
        getOrgItems(props.orgId),
        venuesStore.venues.length ? Promise.resolve() : venuesStore.loadOrgVenues(props.orgId),
      ]);
      source.value = src;
      catalog.value = items;
      name.value = src.gig.name;
    } catch (err) {
      loadError.value = err instanceof Error ? err.message : 'Failed to load gig';
    } finally {
      isLoading.value = false;
    }
  },
  { immediate: true },
);

function close() {
  emit('update:modelValue', false);
}

async function submit() {
  if (!canSubmit.value) return;
  try {
    isSaving.value = true;
    submitError.value = null;
    const newGigId = await gigsStore.duplicateGig(props.orgId, props.gigId, {
      newDate: newDate.value,
      name: name.value,
      timeZone: orgStore.currentOrg?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      timeZoneFor: id => venuesStore.zoneFor(id),
      // With no diffs there's nothing for the mode to change.
      priceMode: priceMode.value ?? 'legacy',
      catalog: catalog.value,
    });
    close();
    emit('duplicated', newGigId);
  } catch (err) {
    submitError.value = err instanceof Error ? err.message : 'Failed to duplicate gig';
  } finally {
    isSaving.value = false;
  }
}
</script>
